import { ThemeSchemaResponse } from '@api';
import { NumericalCellValueStyle } from '@helpers';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { useThemePerformanceDisplay } from '@terminal/hooks/use-theme-performance-display/useThemePerformanceDisplay';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ThemeLogoIcon } from '../../../../views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import styles from './ThemeHeader.module.scss';

const numericalStyleToClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: styles.bullish,
  [NumericalCellValueStyle.Negative]: styles.bearish,
  [NumericalCellValueStyle.Neutral]: styles.neutral,
};

interface ThemeHeaderViewProps {
  themeData: ThemeSchemaResponse;
  badge?: React.ReactNode;
}

export const ThemeHeaderView = ({ themeData, badge }: ThemeHeaderViewProps) => {
  const { t } = useTranslation(['widget', 'insights']);

  const { displayValue, formattedDate, numericalStyle } =
    useThemePerformanceDisplay(themeData.id);

  return (
    <div className={styles.logoAndTitle} data-testid="theme-header">
      <ThemeLogoIcon
        logo_url={themeData.logo_url}
        name={themeData.name}
        type={themeData.type}
      />
      <div className={styles.nameAndReturn}>
        <span
          className={clsx(
            styles.themeType,
            THEME_TAG_COLOR_CLASS_MAP[themeData.type]
          )}
        >
          {t('insights:catalystGeneration.reflexivityGraph.theme', {
            context: themeData.type,
          })}
        </span>
        <div className={styles.titleRow}>
          <span className={styles.titleText}>{themeData.name}</span>
          {badge}
        </div>
        {displayValue !== undefined && (
          <div className={styles.returnRow}>
            <span
              className={clsx(
                styles.returnValue,
                numericalStyleToClass[numericalStyle]
              )}
            >
              {formatPercentageNumber({
                value: displayValue,
                includePercentSign: true,
                addPlus: true,
                formatZero: true,
              })}
            </span>
            {formattedDate && (
              <span className={styles.returnLabel}>
                {t('widget:themeOverview.asOfDate', {
                  date: formattedDate,
                })}
              </span>
            )}
          </div>
        )}
      </div>
      <FollowThemeButton themeId={themeData.id} />
    </div>
  );
};
